import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organizationAPI } from '../../services/api';

interface Organization {
  id: string;
  name: string;
  domain: string;
  logo?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  city?: string;
  country?: string;
  settings?: any;
  status: string;
  created_at: string;
  updated_at: string;
}

interface OrganizationState {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  organization: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchOrganization = createAsyncThunk(
  'organization/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await organizationAPI.get();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch organization');
    }
  }
);

export const createOrganization = createAsyncThunk(
  'organization/create',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await organizationAPI.create(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create organization');
    }
  }
);

export const updateOrganization = createAsyncThunk(
  'organization/update',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await organizationAPI.update(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update organization');
    }
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch organization
      .addCase(fetchOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create organization
      .addCase(createOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload.organization;
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update organization
      .addCase(updateOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload.organization;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = organizationSlice.actions;
export default organizationSlice.reducer;